import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import cytoscape from 'cytoscape';
import fcose from 'cytoscape-fcose';
import { type KnowledgeGraph } from '@lizrd/contracts';
import { getGraph, getGraphCost, getGraphNode, listIntegrations } from '../lib/api';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Spinner } from '../components/ui/spinner';
import { Alert } from '../components/ui/alert';
import { SkeletonText, SkeletonCard } from '../components/ui/skeleton';
import { DomainSummary } from '../components/DomainSummary';
import { CostBreakdown } from '../components/CostBreakdown';
import { CostDrivers } from '../components/CostDrivers';
import { ExplorerGuide } from '../components/ExplorerGuide';
import { NodeTypeIcon } from '../components/node-icons';
import { type Layer, costOf, layerOf, LAYER_COLOR, LAYER_LABEL } from '../lib/graph-visuals';

// Register the force layout once (guard against HMR double-registration).
try {
  cytoscape.use(fcose);
} catch {
  /* already registered */
}

// --- Graph-specific visual model (shared model lives in lib/graph-visuals) ---

const TYPE_SHAPE: Record<string, string> = {
  repository: 'round-rectangle',
  'code.application': 'round-rectangle',
  'container.image': 'round-rectangle',
  'compute.instance': 'round-rectangle',
  'code.dependency': 'ellipse',
  'iac.resource': 'diamond',
  database: 'barrel',
  'cache.cluster': 'barrel',
  'storage.volume': 'barrel',
  'storage.snapshot': 'barrel',
  'storage.bucket': 'barrel',
  'compute.workload': 'hexagon',
  'compute.function': 'hexagon',
  'network.nat': 'diamond',
  'network.address': 'diamond',
  'network.loadbalancer': 'diamond',
  'cloud.account': 'ellipse',
};

const BASE_SIZE: Record<string, number> = {
  repository: 46,
  'code.application': 34,
  'code.dependency': 20,
  'container.image': 28,
  'iac.resource': 26,
  'cloud.account': 40,
  'compute.instance': 30,
  database: 30,
  'storage.volume': 26,
  'compute.workload': 30,
};
const sizeOf = (type: string, cost: number): number =>
  cost > 0 ? Math.min(72, 26 + Math.sqrt(cost) * 1.7) : (BASE_SIZE[type] ?? 24);

const CORRELATION_EDGES = new Set(['OWNS', 'PROVISIONS', 'RUNS_ON']);
const DASHED_EDGES = new Set(['DEPENDS_ON', 'BELONGS_TO', 'ATTACHED_TO']);

// Layers offered as one-click filters, in Code→Cloud order. Short labels for chips.
const FILTER_LAYERS: Layer[] = ['code', 'iac', 'cloud', 'runtime', 'other'];
const LAYER_SHORT: Record<Layer, string> = {
  code: 'Code',
  iac: 'IaC',
  cloud: 'Cloud',
  runtime: 'Runtime',
  other: 'Other',
};

type Mode = 'explore' | 'flow';
type GraphNode = KnowledgeGraph['nodes'][number];

// The relationship graph is kept but hidden while cost views (treemap + flow) are
// the focus; flip to true to bring the Domain Explorer graph back.
const SHOW_GRAPH = false;

// The long-tail node types collapsed into expandable clusters (one per repo) so
// the graph starts browsable instead of a hairball of hundreds of leaf nodes.
const COLLAPSIBLE_TYPES = new Set(['code.dependency', 'iac.resource']);
const CLUSTER_META: Record<string, { label: string }> = {
  'code.dependency': { label: 'dependencies' },
  'iac.resource': { label: 'Terraform resources' },
};

/** Nearest repository for every node (multi-source BFS over undirected edges),
 * so long-tail nodes can be clustered under the repo they belong to. */
function repoOwnership(graph: KnowledgeGraph): Map<string, string> {
  const adj = new Map<string, string[]>();
  for (const e of graph.edges) {
    (adj.get(e.from) ?? adj.set(e.from, []).get(e.from)!).push(e.to);
    (adj.get(e.to) ?? adj.set(e.to, []).get(e.to)!).push(e.from);
  }
  const owner = new Map<string, string>();
  const queue: string[] = [];
  for (const n of graph.nodes)
    if (n.type === 'repository') {
      owner.set(n.id, n.id);
      queue.push(n.id);
    }
  for (let i = 0; i < queue.length; i++) {
    for (const nb of adj.get(queue[i]!) ?? []) {
      if (!owner.has(nb)) {
        owner.set(nb, owner.get(queue[i]!)!);
        queue.push(nb);
      }
    }
  }
  return owner;
}

/**
 * Build Cytoscape elements with the long tail collapsed into expandable clusters.
 * Each collapsible type becomes one cluster node per owning repo; edges to/from a
 * collapsed member reroute onto the cluster node and dedupe — so a collapsed
 * Terraform cluster still shows aggregate PROVISIONS edges to the cloud resources
 * it creates. Expanding a cluster (via `expanded`) reveals its members.
 */
function buildElements(
  graph: KnowledgeGraph,
  expanded: Set<string>,
): cytoscape.ElementDefinition[] {
  const owner = repoOwnership(graph);
  const byId = new Map(graph.nodes.map((n) => [n.id, n] as const));
  const clusterKey = (n: GraphNode): string | null => {
    if (!COLLAPSIBLE_TYPES.has(n.type)) return null;
    const key = `cluster:${n.type}:${owner.get(n.id) ?? 'none'}`;
    return expanded.has(key) ? null : key; // expanded → render members individually
  };

  const clusters = new Map<string, { type: string; count: number }>();
  const shown = new Set<string>();
  for (const n of graph.nodes) {
    const ck = clusterKey(n);
    if (ck) (clusters.get(ck) ?? clusters.set(ck, { type: n.type, count: 0 }).get(ck)!).count++;
    else shown.add(n.id);
  }

  const nodes: cytoscape.ElementDefinition[] = [];
  for (const n of graph.nodes) {
    if (!shown.has(n.id)) continue;
    const layer = layerOf(n.type);
    nodes.push({
      data: {
        id: n.id,
        label: n.name,
        type: n.type,
        layer,
        color: LAYER_COLOR[layer],
        shape: TYPE_SHAPE[n.type] ?? 'ellipse',
        size: sizeOf(n.type, costOf(n.properties)),
      },
    });
  }
  for (const [key, c] of clusters) {
    const meta = CLUSTER_META[c.type]!;
    const layer = layerOf(c.type);
    nodes.push({
      data: {
        id: key,
        isCluster: 1,
        label: `${c.count} ${meta.label}`,
        type: c.type,
        layer,
        color: LAYER_COLOR[layer],
      },
    });
  }

  const renderId = (id: string): string => {
    const n = byId.get(id);
    return (n && clusterKey(n)) ?? id;
  };
  const seen = new Set<string>();
  const edges: cytoscape.ElementDefinition[] = [];
  for (const e of graph.edges) {
    const source = renderId(e.from);
    const target = renderId(e.to);
    if (source === target) continue; // intra-cluster edge
    const key = `${source}|${e.type}|${target}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const corr = CORRELATION_EDGES.has(e.type);
    edges.push({
      data: {
        id: key,
        source,
        target,
        type: e.type,
        correlation: corr ? 1 : 0,
        style: corr ? 'solid' : DASHED_EDGES.has(e.type) ? 'dashed' : 'solid',
      },
    });
  }

  return [...nodes, ...edges];
}

const STYLE = [
  {
    // Collapsed cluster: a dashed pill sized to its "142 dependencies" label.
    selector: 'node[?isCluster]',
    style: {
      shape: 'round-rectangle',
      'background-color': 'data(color)',
      'background-opacity': 0.16,
      'border-width': 1.5,
      'border-color': 'data(color)',
      'border-style': 'dashed',
      width: 'label',
      height: 'label',
      padding: '10px',
      label: 'data(label)',
      color: '#e9f1ed',
      'font-size': 11,
      'font-weight': 'bold',
      'text-valign': 'center',
      'text-halign': 'center',
    },
  },
  {
    selector: 'node[!isCluster]',
    style: {
      'background-color': 'data(color)',
      'background-opacity': 0.9,
      shape: 'data(shape)',
      width: 'data(size)',
      height: 'data(size)',
      'border-width': 1.5,
      'border-color': 'rgba(9,11,17,0.9)',
      label: 'data(label)',
      color: '#c9cedb',
      'font-size': 9,
      'text-valign': 'bottom',
      'text-margin-y': 3,
      'text-max-width': '120px',
      'text-wrap': 'ellipsis',
      'min-zoomed-font-size': 7,
    },
  },
  {
    selector: 'edge',
    style: {
      width: 'mapData(correlation, 0, 1, 1.1, 2.6)',
      'line-style': 'data(style)',
      'target-arrow-shape': 'triangle',
      'curve-style': 'bezier',
      'arrow-scale': 0.7,
      opacity: 0.55,
    },
  },
  {
    selector: 'edge[correlation = 1]',
    style: { 'line-color': '#22d3ee', 'target-arrow-color': '#22d3ee', opacity: 0.9 },
  },
  {
    selector: 'edge[correlation = 0]',
    style: { 'line-color': '#3a4252', 'target-arrow-color': '#3a4252' },
  },
  { selector: '.dim', style: { opacity: 0.12 } },
  { selector: 'node.match', style: { 'border-width': 3, 'border-color': '#fde047' } },
  { selector: 'node:selected', style: { 'border-width': 3, 'border-color': '#ffffff' } },
] as unknown as cytoscape.StylesheetCSS[];

/**
 * The Domain Explorer (KG-S8): an interactive Cytoscape graph of the
 * Customer Knowledge Graph. Nodes coloured by layer (Code/IaC/Cloud/Runtime),
 * shaped by type, sized by cost; the long tail (dependencies, Terraform) is
 * collapsed into clickable clusters so the graph starts browsable. Force physics
 * (Explore) or Code→Cloud columns (Flow); correlation edges highlighted cyan;
 * filters, search, legend and a node-detail card.
 */
/** A page section with a scroll anchor targeted by the guide's step links. */
function StepAnchor({ id, children }: { id: string; children: ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      {children}
    </section>
  );
}

export function ExplorerPage() {
  const { domainId = '' } = useParams();
  const [mode, setMode] = useState<Mode>('explore');
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  // Cluster ids the user has expanded (dependencies / Terraform drill-in).
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // A "See why" click in the guide focuses (opens + scrolls to) a driver row.
  // The nonce lets the same driver be re-focused on repeat clicks.
  const [focusDriver, setFocusDriver] = useState<{ id: string; nonce: number } | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const cyRef = useRef<cytoscape.Core | null>(null);

  // Poll while the Explorer is open so scheduled syncs surface without a manual
  // reload (KGE). Structural sharing keeps the data reference stable when nothing
  // changed, so the Cytoscape layout only rebuilds on a real graph change.
  const graph = useQuery({
    queryKey: ['graph', domainId],
    queryFn: () => getGraph(domainId),
    enabled: !!domainId,
    refetchInterval: 60_000,
  });
  const cost = useQuery({
    queryKey: ['graph-cost', domainId],
    queryFn: () => getGraphCost(domainId),
    enabled: !!domainId,
    refetchInterval: 60_000,
  });
  const node = useQuery({
    queryKey: ['graph-node', domainId, selectedId],
    queryFn: () => getGraphNode(domainId, selectedId!),
    enabled: !!domainId && !!selectedId,
  });

  const integrations = useQuery({
    queryKey: ['integrations', domainId],
    queryFn: () => listIntegrations(domainId),
    enabled: !!domainId,
  });

  const byType = graph.data?.stats.byType ?? {};
  const types = useMemo(() => Object.keys(byType).sort(), [byType]);

  // Present node types grouped by layer, for the layer-level filter chips.
  const typesByLayer = useMemo(() => {
    const m: Partial<Record<Layer, string[]>> = {};
    for (const t of types) (m[layerOf(t)] ??= []).push(t);
    return m;
  }, [types]);
  const layerCount = (l: Layer) =>
    (typesByLayer[l] ?? []).reduce((sum, t) => sum + (byType[t] ?? 0), 0);
  const layerAllHidden = (l: Layer) => {
    const ts = typesByLayer[l] ?? [];
    return ts.length > 0 && ts.every((t) => hidden.has(t));
  };
  const toggleLayer = (l: Layer) =>
    setHidden((h) => {
      const ts = typesByLayer[l] ?? [];
      const next = new Set(h);
      if (ts.every((t) => next.has(t))) ts.forEach((t) => next.delete(t));
      else ts.forEach((t) => next.add(t));
      return next;
    });

  // Most recent successful sync across the domain's integrations.
  const lastSyncedAt = useMemo(() => {
    let latest: string | null = null;
    for (const i of integrations.data ?? []) {
      if (i.lastSyncedAt && (!latest || i.lastSyncedAt > latest)) latest = i.lastSyncedAt;
    }
    return latest;
  }, [integrations.data]);

  const runLayout = (cy: cytoscape.Core, m: Mode) => {
    if (m === 'flow') {
      const cols: Layer[] = ['code', 'iac', 'cloud', 'runtime', 'other'];
      const byLayer = new Map<Layer, cytoscape.NodeSingular[]>();
      cy.nodes().forEach((n) => {
        const l = (n.data('layer') as Layer) ?? 'other';
        (byLayer.get(l) ?? byLayer.set(l, []).get(l)!).push(n);
      });
      const positions: Record<string, cytoscape.Position> = {};
      cols.forEach((layer, ci) => {
        (byLayer.get(layer) ?? []).forEach((n, i) => {
          positions[n.id()] = { x: 140 + ci * 300, y: 80 + i * 68 };
        });
      });
      cy.layout({
        name: 'preset',
        positions,
        fit: true,
        padding: 40,
      } as cytoscape.LayoutOptions).run();
    } else {
      cy.layout({
        name: 'fcose',
        quality: 'default',
        animate: false,
        idealEdgeLength: 95,
        nodeRepulsion: 9000,
        gravity: 0.3,
        nestingFactor: 0.9,
        padding: 30,
      } as cytoscape.LayoutOptions).run();
    }
  };

  // Init once.
  useEffect(() => {
    if (!containerRef.current || cyRef.current) return;
    const cy = cytoscape({ container: containerRef.current, style: STYLE, wheelSensitivity: 0.2 });
    // Tapping a cluster expands/collapses it; tapping a resource selects it.
    cy.on('tap', 'node[?isCluster]', (e) => {
      const id = e.target.id();
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    });
    cy.on('tap', 'node[!isCluster]', (e) => setSelectedId(e.target.id()));
    cy.on('tap', (e) => {
      if (e.target === cy) setSelectedId(null);
    });
    cy.on('mouseover', 'node', (e) => {
      const keep = e.target.closedNeighborhood();
      cy.elements().not(keep).addClass('dim');
    });
    cy.on('mouseout', 'node', () => cy.elements().removeClass('dim'));
    cyRef.current = cy;
    return () => {
      cy.destroy();
      cyRef.current = null;
    };
  }, []);

  // Rebuild elements when the graph, expansion, or mode changes.
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || !graph.data) return;
    cy.elements().remove();
    cy.add(buildElements(graph.data, expanded));
    runLayout(cy, mode);
  }, [graph.data, expanded, mode]);

  // Type-filter visibility.
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.batch(() => {
      cy.nodes().forEach((n) => {
        n.style('display', hidden.has(n.data('type') as string) ? 'none' : 'element');
      });
    });
  }, [hidden, graph.data, mode, expanded]);

  // Search highlight.
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    const q = search.trim().toLowerCase();
    cy.batch(() => {
      cy.elements().removeClass('dim match');
      if (!q) return;
      const matches = cy
        .nodes('[!isCluster]')
        .filter((n) => (n.data('label') as string).toLowerCase().includes(q));
      if (!matches.length) return;
      cy.elements().addClass('dim');
      matches.removeClass('dim').addClass('match');
      matches.closedNeighborhood().removeClass('dim');
    });
    if (q) cy.fit(cy.nodes('.match'), 80);
  }, [search, graph.data, mode, expanded]);

  const toggleType = (t: string) =>
    setHidden((h) => {
      const next = new Set(h);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });

  return (
    <div className="space-y-4" data-tour="explorer">
      {/* The mental model: overview → cost → why. */}
      <ExplorerGuide
        graph={graph.data}
        onSeeWhy={(id) => setFocusDriver((f) => ({ id, nonce: (f?.nonce ?? 0) + 1 }))}
      />

      {/* First load (no cached graph yet) → skeletons for the overview + cost + drivers,
          so the page never flashes blank cards while the graph is fetched. */}
      {graph.isLoading ? (
        <>
          <SkeletonCard lines={5} />
          <SkeletonCard lines={6} />
          <SkeletonCard lines={4} />
        </>
      ) : (
        <>
          {/* Large domain: the resource list below renders a capped set (M2). The cost
              total, breakdown, tree-map, and type counts are all computed server-side over
              every resource and stay complete — only the Cost Drivers detail covers the
              rendered subset. Say so honestly. */}
          {graph.data?.stats.truncated && (
            <Alert variant="warning">
              This domain has{' '}
              {(graph.data.stats.totalNodeCount ?? graph.data.stats.nodeCount).toLocaleString()}{' '}
              resources — more than the Explorer lists at once (showing{' '}
              {graph.data.stats.nodeCount.toLocaleString()}). Your cost total, breakdown, and
              tree-map cover <em>all</em> of them; only the Cost Drivers list below is limited to
              the rendered subset. Narrow with a type filter or search for the full driver detail.
            </Alert>
          )}

          {/* Step 1 — Overview: what you run (syncs run automatically on a schedule) */}
          <StepAnchor id="explorer-overview">
            <DomainSummary
              envId={domainId}
              graph={graph.data ?? null}
              totalMonthlyCost={cost.data?.totalMonthlyCost ?? 0}
              lastSyncedAt={lastSyncedAt}
            />
          </StepAnchor>

          {/* Step 2 — Cost: breakdown / tree-map / trend (server-side aggregate, uncapped) */}
          <StepAnchor id="explorer-cost">
            <CostBreakdown envId={domainId} />
          </StepAnchor>

          {/* Step 3 — Why: the explained cost drivers */}
          {graph.data && (
            <StepAnchor id="explorer-drivers">
              <CostDrivers graph={graph.data} envId={domainId} focus={focusDriver} />
            </StepAnchor>
          )}
        </>
      )}

      {/* 3. Domain Explorer — kept but hidden for now (SHOW_GRAPH). */}
      {SHOW_GRAPH && (
        <>
          <div>
            <h2 className="font-display text-base font-bold text-bone">Domain Explorer</h2>
            <p className="text-xs text-muted">
              Code, the infrastructure it declares, and what runs — the long tail is grouped into{' '}
              <span className="text-haze">clusters you click to expand</span>.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex overflow-hidden rounded-sm border border-border text-xs">
              {(['explore', 'flow'] as Mode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`px-3 py-1.5 ${mode === m ? 'bg-iris text-white' : 'bg-surface text-muted hover:text-bone'}`}
                >
                  {m === 'explore' ? '✦ Explore' : '⇉ Flow'}
                </button>
              ))}
            </div>
            <div className="w-52">
              <Input
                placeholder="Search the ecosystem…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap gap-1">
              {FILTER_LAYERS.filter((l) => (typesByLayer[l]?.length ?? 0) > 0).map((l) => (
                <button
                  key={l}
                  onClick={() => toggleLayer(l)}
                  title={`Toggle the ${LAYER_LABEL[l]} layer`}
                  className={`flex items-center gap-1 rounded-sm border px-2 py-1 text-[0.7rem] font-semibold transition-colors ${
                    layerAllHidden(l)
                      ? 'border-border bg-surface text-muted/50'
                      : 'border-border bg-surface-2 text-bone'
                  }`}
                >
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: LAYER_COLOR[l] }}
                  />
                  {LAYER_SHORT[l]} {layerCount(l)}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1">
              {types.map((t) => (
                <button
                  key={t}
                  onClick={() => toggleType(t)}
                  className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[0.65rem] transition-colors ${
                    hidden.has(t)
                      ? 'border-border bg-surface text-muted/50 line-through'
                      : 'border-border bg-surface-2 text-bone'
                  }`}
                >
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: LAYER_COLOR[layerOf(t)] }}
                  />
                  <NodeTypeIcon type={t} className="h-3.5 w-3.5 text-muted" /> {t} {byType[t]}
                </button>
              ))}
            </div>
            {graph.data && (
              <span className="ml-auto text-xs text-muted">
                {graph.data.stats.truncated ? (
                  <span
                    className="text-warning"
                    title="This domain has more nodes than the Explorer renders at once. Use search or a type filter to narrow the view."
                  >
                    showing {graph.data.stats.nodeCount.toLocaleString()} of{' '}
                    {(
                      graph.data.stats.totalNodeCount ?? graph.data.stats.nodeCount
                    ).toLocaleString()}{' '}
                    nodes
                  </span>
                ) : (
                  <>{graph.data.stats.nodeCount.toLocaleString()} nodes</>
                )}{' '}
                · {graph.data.stats.edgeCount.toLocaleString()} edges · v
                {graph.data.stats.graphVersion}
              </span>
            )}
          </div>

          <div className="flex gap-3">
            <Card className="relative flex-1 overflow-hidden p-0" style={{ height: 620 }}>
              <div
                ref={containerRef}
                className="h-full w-full"
                style={{ background: 'radial-gradient(circle at 50% 40%, #12151d, #0b0d13)' }}
              />
              {graph.isLoading && (
                <div className="absolute inset-0 grid place-items-center gap-3 text-sm text-muted">
                  <div className="flex flex-col items-center gap-3">
                    <Spinner size="lg" />
                    Loading graph…
                  </div>
                </div>
              )}
              {graph.data && graph.data.nodes.length === 0 && (
                <div className="absolute inset-0 grid place-items-center px-6 text-center text-sm text-muted">
                  No graph yet — connect an integration and run a sync to populate this
                  domain&rsquo;s knowledge graph.
                </div>
              )}
              <Legend />
            </Card>

            {selectedId && (
              <Card
                className="w-72 shrink-0 space-y-3 overflow-auto p-4 text-sm"
                style={{ height: 620 }}
              >
                {node.isLoading && <SkeletonText lines={5} className="mt-1" />}
                {node.data && (
                  <>
                    <div>
                      <p className="font-display text-base font-bold text-bone">
                        <NodeTypeIcon
                          type={node.data.type as string}
                          className="inline-block h-4 w-4 align-text-bottom text-muted"
                        />{' '}
                        {node.data.name}
                      </p>
                      <p className="text-xs text-muted">
                        {node.data.type} · {LAYER_LABEL[layerOf(node.data.type)]} ·{' '}
                        {node.data.provider}
                      </p>
                    </div>
                    <MetaTable metadata={node.data.properties} />
                    <details className="text-xs">
                      <summary className="cursor-pointer text-muted">Provenance</summary>
                      <pre className="mt-1 overflow-auto rounded-sm bg-surface-2 p-2 text-[0.65rem]">
                        {JSON.stringify(node.data.provenance, null, 2)}
                      </pre>
                    </details>
                    <Button size="sm" variant="outline" onClick={() => setSelectedId(null)}>
                      Close
                    </Button>
                  </>
                )}
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function MetaTable({ metadata }: { metadata: Record<string, unknown> }) {
  const entries = Object.entries(metadata).filter(
    ([, v]) => v !== null && v !== undefined && v !== '',
  );
  if (entries.length === 0) return <p className="text-xs text-muted">No metadata.</p>;
  return (
    <dl className="space-y-1 text-xs">
      {entries.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-2">
          <dt className="text-muted">{k}</dt>
          <dd className="max-w-[62%] truncate text-right text-bone" title={stringify(v)}>
            {stringify(v)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function stringify(v: unknown): string {
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
}

function Legend() {
  const layers: Layer[] = ['code', 'iac', 'cloud', 'runtime'];
  return (
    <div className="pointer-events-none absolute right-2 top-2 flex flex-col gap-1 rounded-sm bg-surface/80 p-2 text-[0.62rem]">
      {layers.map((l) => (
        <span key={l} className="flex items-center gap-1.5 text-muted">
          <span
            className="inline-block h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: LAYER_COLOR[l] }}
          />
          {LAYER_LABEL[l]}
        </span>
      ))}
      <span className="mt-1 flex items-center gap-1.5 text-cyan-300">
        <span className="inline-block h-0.5 w-4" style={{ backgroundColor: '#22d3ee' }} />
        correlation
      </span>
    </div>
  );
}
